import { useTranslation } from 'react-i18next';
import { FormikProvider, useFormik } from 'formik';
import _ from 'lodash';
import { useState } from 'react';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from 'generated/graphql';
import Button, { ButtonSize } from 'packages/kakapo-ui-kit/components/editors/Button';
import Paragraph from 'packages/kakapo-ui-kit/components/common/Paragraph';
import Modal, {
	ModalBackground,
	ModalContent,
} from 'packages/kakapo-ui-kit/components/modals/Modal';
import ModalHeader, {
	ModalHeaderTitle,
} from 'packages/kakapo-ui-kit/components/modals/ModalHeader';
import Select from 'packages/kakapo-ui-kit/components/forms/controls/Select';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import { CompetencyLevelsInput } from 'packages/kakapo-ui-application/pages/evaluation-submissions/input/CompetencyInput';

const Row = styled(Flex)``;

const Step = styled(Box)`
	width: 20px;
	flex-grow: 0;
	flex-shrink: 0;
	color: ${(props) => props.theme.colors.slateBlue};
	font-size: 16px;
`;

const Content = styled(Box)`
	flex-shrink: 1;
	flex-grow: 1;
`;

const Summary = styled(Box)`
	border-radius: 4px;
	font-size: 16px;
	color: ${(props) => props.theme.colors.slateBlue};
`;

export default function JobCompetencyEditorModal(props: {
	jobTitle: graphql.JobTitle;
	onDismiss: () => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const jobLevels = graphql.useJobLevelsQuery({ variables: { first: 1000 } });
	const competencies = graphql.useCompetenciesWithLevelsQuery({
		variables: { isArchived: false, first: 1000 },
	});

	const [attach] = graphql.useAttachJobCompetencyMutation();

	const [levelID, setLevelID] = useState('*');
	const [competencyID, setCompetencyID] = useState<string | undefined>(undefined);

	const level = jobLevels.data?.jobLevels.edges
		.map(({ node }) => node)
		.find((node) => node.id === levelID);

	const competency = competencies.data?.competencies.edges
		.map(({ node }) => node)
		.find((node) => node.id === competencyID);

	const formik = useFormik({
		initialValues: { competency: { competency: undefined as number | undefined } },
		onSubmit: () => {},
	});

	const competencyLevel =
		formik.values.competency.competency !== undefined
			? competency?.levels[formik.values.competency.competency]
			: undefined;

	const save = async () => {
		await attach({
			variables: {
				input: {
					jobTitleID: props.jobTitle.id,
					jobLevelID: levelID === '*' ? undefined : levelID,
					competencyID: competencyID!,
					competencyLevel: formik.values.competency.competency!,
				},
			},
		});

		props.onDismiss();
	};

	return (
		<ModalBackground>
			<Modal>
				<ModalHeader onCancel={() => props.onDismiss()}>
					<ModalHeaderTitle>{t('settings.jobCompetencies.create.title')}</ModalHeaderTitle>
				</ModalHeader>
				<ModalContent>
					<FormikProvider value={formik}>
						<Row>
							<Step>1.</Step>
							<Content>
								<Box mb={2}>
									<Paragraph color="slateBlue">
										{t('settings.jobCompetencies.create.levelLabel')}
									</Paragraph>
								</Box>
								<Select
									defaultValue={{
										value: '*',
										label: t('settings.jobCompetencies.allLevels'),
									}}
									options={[
										{
											value: '*',
											label: t('settings.jobCompetencies.allLevels'),
										},
										..._.sortBy(
											jobLevels.data?.jobLevels.edges.map(({ node }) => ({
												value: node.id,
												label: localize(node.title).text,
											})) ?? [],
											'label',
										),
									]}
									onChange={(option) => setLevelID(option!.value)}
								/>
							</Content>
						</Row>
						<Row>
							<Step>2.</Step>
							<Content>
								<Box mb={2}>
									<Paragraph color="slateBlue">
										{t('settings.jobCompetencies.create.competencyLabel')}
									</Paragraph>
								</Box>
								<Select
									options={
										_.sortBy(
											competencies.data?.competencies.edges,
											({ node }) => localize(node.title).text,
										).map(({ node }) => ({
											value: node.id,
											label: localize(node.title).text,
										})) ?? []
									}
									onChange={(option) => {
										setCompetencyID(option!.value);
										formik.setFieldValue('competency.competency', undefined);
									}}
								/>
							</Content>
						</Row>
						{competency && (
							<Row>
								<Step>3.</Step>
								<Content>
									<Box mb={2}>
										<Paragraph color="slateBlue">
											{t('settings.jobCompetencies.create.competencyLevelLabel')}
										</Paragraph>
									</Box>
									<CompetencyLevelsInput
										competency={competency as graphql.Competency}
										name="competency"
										party={graphql.EvaluationSubmissionParty.Employee}
									/>
								</Content>
							</Row>
						)}
						{competency && competencyLevel && (
							<>
								{level ? (
									<Summary p={2} mt={4}>
										<strong style={{ color: 'black' }}>
											{t('settings.jobCompetencies.create.summaryLabel')}:
										</strong>{' '}
										{t('settings.jobCompetencies.create.summaryWithLevel', {
											title: localize(props.jobTitle.title).text,
											level: localize(level.title).text,
											competencyLevel: localize(competencyLevel.level.title).text,
											competency: localize(competency.title).text,
										})}
									</Summary>
								) : (
									<Summary p={2} mt={4}>
										<strong style={{ color: 'black' }}>
											{t('settings.jobCompetencies.create.summaryLabel')}:
										</strong>{' '}
										{t('settings.jobCompetencies.create.summaryWithoutLevel', {
											title: localize(props.jobTitle.title).text,
											competencyLevel: localize(competencyLevel.level.title).text,
											competency: localize(competency.title).text,
										})}
									</Summary>
								)}
								<Flex justifyContent="flex-end">
									<Box mt={3}>
										<Button size={ButtonSize.Small} onClick={save}>
											{t('common.save')}
										</Button>
									</Box>
								</Flex>
							</>
						)}
					</FormikProvider>
				</ModalContent>
			</Modal>
		</ModalBackground>
	);
}
